/**
 * Editor for a choice's feedback variants chain. V4's per-state feedback
 * lives here: the runtime walks `choice.feedbackVariants` top-to-bottom
 * against the post-effects state, and the first matching entry's `text`
 * is shown to the learner.
 *
 * Identical chain pattern to DestinationChainEditor and SceneVariantsEditor.
 * UX modes:
 *   - Bare mode: only the fallback exists. Renders as a single feedback
 *     textarea — the placement authors recognize from "Optional feedback
 *     shown before transition" in V3.
 *   - Chain mode: at least one conditional entry exists. Each entry is a
 *     card with "Use this feedback if" header + ConditionBuilder + textarea.
 *     The fallback gets a "Default feedback" header.
 *
 * Feedback text may be empty — `""` is a valid choice that means "no
 * feedback shown for this state." That's by design (some authors want
 * feedback for *some* states but silence otherwise), and the validator
 * doesn't flag it.
 */
import { ChevronDown, ChevronUp, MessageSquare, X } from 'lucide-react';
import { newId, type FeedbackVariant, type VariableDecl } from '@branchworks/shared';
import { ConditionBuilder } from './ConditionBuilder';

interface FeedbackVariantsEditorProps {
  feedbackVariants: FeedbackVariant[];
  variables: Record<string, VariableDecl>;
  onChange: (next: FeedbackVariant[]) => void;
}

export function FeedbackVariantsEditor({
  feedbackVariants,
  variables,
  onChange,
}: FeedbackVariantsEditorProps) {
  const safe = normalize(feedbackVariants);
  const lastIdx = safe.length - 1;
  const conditionalEntries = safe.slice(0, lastIdx);
  const fallback = safe[lastIdx];
  const hasConditionals = conditionalEntries.length > 0;
  const hasVariables = Object.keys(variables).length > 0;

  const updateEntry = (idx: number, patch: Partial<FeedbackVariant>) => {
    onChange(safe.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const removeEntry = (idx: number) => {
    onChange(safe.filter((_, i) => i !== idx));
  };

  const updateConditionalCondition = (idx: number, cond: FeedbackVariant['condition']) => {
    if (cond === null) {
      removeEntry(idx);
    } else {
      updateEntry(idx, { condition: cond });
    }
  };

  const addConditional = () => {
    if (!hasVariables) return;
    const firstVar = Object.keys(variables)[0];
    const decl = variables[firstVar];
    const newEntry: FeedbackVariant = {
      id: newId('vf'),
      condition: {
        combinator: 'all',
        comparisons: [{ variable: firstVar, op: '==', value: decl.default }],
      },
      text: '',
    };
    onChange([...conditionalEntries, newEntry, fallback]);
  };

  const moveEntry = (idx: number, direction: -1 | 1) => {
    const swap = idx + direction;
    if (swap < 0 || swap >= conditionalEntries.length) return;
    const next = conditionalEntries.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange([...next, fallback]);
  };

  return (
    <div className="space-y-2">
      {conditionalEntries.map((v, i) => (
        <div
          key={v.id || i}
          className="rounded border p-2 space-y-1.5 border-cream-border bg-cream-2"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest text-ink-2">
              Use this feedback if
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                onClick={() => moveEntry(i, -1)}
                disabled={i === 0}
                className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up (this feedback is checked earlier)"
                aria-label="Move conditional feedback up"
              >
                <ChevronUp size={11} />
              </button>
              <button
                onClick={() => moveEntry(i, 1)}
                disabled={i === conditionalEntries.length - 1}
                className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down"
                aria-label="Move conditional feedback down"
              >
                <ChevronDown size={11} />
              </button>
              <button
                onClick={() => removeEntry(i)}
                className="p-0.5 rounded hover:bg-cream text-terracotta-dark"
                title="Remove conditional feedback"
                aria-label="Remove conditional feedback"
              >
                <X size={11} />
              </button>
            </div>
          </div>
          <ConditionBuilder
            condition={v.condition}
            variables={variables}
            onChange={(c) => updateConditionalCondition(i, c)}
            emptyLabel="Empty (will be removed)"
            addLabel="Add comparison"
          />
          {renderFeedbackBody(v, (patch) => updateEntry(i, patch))}
        </div>
      ))}

      {hasConditionals ? (
        <div className="rounded border p-2 space-y-1.5 border-cream-border bg-cream-2">
          <div className="text-[10px] uppercase tracking-widest text-ink-2">
            Default feedback
          </div>
          {renderFeedbackBody(fallback, (patch) => updateEntry(lastIdx, patch))}
        </div>
      ) : (
        renderFeedbackBody(fallback, (patch) => updateEntry(lastIdx, patch))
      )}

      <button
        onClick={addConditional}
        disabled={!hasVariables}
        className="text-xs flex items-center gap-1 hover:underline disabled:opacity-40 disabled:cursor-not-allowed text-ink"
        title={
          hasVariables
            ? 'Add a state-conditional feedback message. Higher entries are checked first.'
            : 'Declare variables before adding conditional feedback.'
        }
      >
        <MessageSquare size={11} /> Add conditional feedback
      </button>
    </div>
  );
}

function renderFeedbackBody(
  variant: FeedbackVariant,
  onChange: (patch: Partial<FeedbackVariant>) => void,
) {
  // 2 rows — feedback is typically short ("Good call. Early objective
  // evidence is exactly what this needs."). Authors who want longer feedback
  // get scroll instead of dominating the panel.
  return (
    <textarea
      value={variant.text || ''}
      onChange={(e) => onChange({ text: e.target.value })}
      rows={2}
      placeholder="Optional feedback shown before transition (empty = silent)"
      className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-xs italic leading-relaxed"
    />
  );
}

function normalize(variants: FeedbackVariant[]): FeedbackVariant[] {
  const list = (variants || []).slice();
  if (list.length === 0) {
    list.push({ id: newId('vf'), condition: null, text: '' });
  }
  for (let i = 0; i < list.length; i++) {
    if (!list[i].id) list[i] = { ...list[i], id: newId('vf') };
  }
  const lastIdx = list.length - 1;
  if (list[lastIdx].condition !== null) {
    list[lastIdx] = { ...list[lastIdx], condition: null };
  }
  return list;
}
